// Query/body schemas for /api/v1. The API validates with these and the
// clients build requests from the inferred types.

import { z } from 'zod'
import { ORDER_STATUSES, RETAILERS } from './enums'

const retailer = z.enum(RETAILERS)
const status = z.enum(ORDER_STATUSES)

export const pageQuery = z.object({
  offset: z.coerce.number().int().min(0).default(0),
  limit: z.coerce.number().int().min(1).max(200).default(50),
})

export const ORDER_SORTS = ['placed', 'total', 'status', 'updated'] as const
export type OrderSort = (typeof ORDER_SORTS)[number]

export const orderListQuery = pageQuery.extend({
  search: z.string().trim().max(200).optional(),
  retailer: retailer.optional(),
  status: status.optional(),
  email: z.string().trim().max(320).optional(),
  sort: z.enum(ORDER_SORTS).default('placed'),
  desc: z
    .enum(['true', 'false'])
    .default('true')
    .transform((v) => v === 'true'),
})
export type OrderListQuery = z.input<typeof orderListQuery>

/** IANA zone the client lives in; day boundaries are computed there. */
export const timeZone = z
  .string()
  .max(64)
  .refine((tz) => {
    try {
      new Intl.DateTimeFormat('en-US', { timeZone: tz })
      return true
    } catch {
      return false
    }
  }, 'Unknown time zone')
  .default('UTC')

export const dashboardQuery = z.object({
  tz: timeZone,
  days: z.coerce.number().int().refine((d) => [1, 7, 30, 365].includes(d), {
    message: 'days must be 1, 7, 30 or 365',
  }).default(30),
})
export type DashboardQuery = z.input<typeof dashboardQuery>

export const incomingQuery = z.object({ tz: timeZone })
export type IncomingQuery = z.input<typeof incomingQuery>

export const orderLookupQuery = z.object({ retailer: retailer.optional() })

export const trackingLookupQuery = z.object({
  code: z.string().trim().min(1).max(200).describe('What the scanner read off the label, as-is; the API strips routing data'),
})
export type TrackingLookupQuery = z.input<typeof trackingLookupQuery>

export const markDeliveredBody = z.object({
  retailer: retailer.describe('Order numbers are unique per retailer'),
  trackingNumber: z.string().trim().max(64).optional().describe('`TrackingMatch.trackingNumber` when the delivery was confirmed by scanning'),
})
export type MarkDeliveredBody = z.input<typeof markDeliveredBody>

export const DROP_OUTCOMES = ['success', 'cancelled'] as const
/** recent: newest first · orders: biggest first · best/worst: by share that went through. */
export const DROP_SORTS = ['recent', 'orders', 'best', 'worst'] as const
export type DropSort = (typeof DROP_SORTS)[number]
export const dropListQuery = pageQuery.extend({
  retailer: retailer.optional(),
  outcome: z.enum(DROP_OUTCOMES).optional(),
  search: z.string().trim().max(200).optional(),
  sort: z.enum(DROP_SORTS).default('recent'),
})
export type DropListQuery = z.input<typeof dropListQuery>

export const ACCOUNT_SORTS = ['retailer', 'orders', 'success', 'failed', 'lastSeen'] as const
export const accountListQuery = pageQuery.extend({
  search: z.string().trim().max(200).optional(),
  retailer: retailer.optional(),
  sort: z.enum(ACCOUNT_SORTS).default('lastSeen'),
  desc: z
    .enum(['true', 'false'])
    .default('true')
    .transform((v) => v === 'true'),
})
export type AccountListQuery = z.input<typeof accountListQuery>

/** Drop ids are `retailer~normalized-name~epochSeconds`, URL-safe. */
export const dropId = {
  encode: (retailer: string, normalizedName: string, startedAt: string) =>
    `${retailer}~${encodeURIComponent(normalizedName)}~${Math.floor(Date.parse(startedAt) / 1000)}`,
  decode: (id: string) => {
    const [r, name, secs] = id.split('~')
    const parsed = z
      .object({ retailer, normalizedName: z.string().min(1), startedAt: z.coerce.number().int().positive() })
      .safeParse({ retailer: r, normalizedName: name ? decodeURIComponent(name) : '', startedAt: secs })
    return parsed.success ? parsed.data : null
  },
}

/**
 * Vault (workspace) names: letters, digits and single spaces, 3–32 characters,
 * at least one letter. The slug is derived from the name (see `vaultSlug`),
 * so two Vaults can't share a name in any case or spacing.
 */
export const VAULT_NAME_MIN = 3
export const VAULT_NAME_MAX = 32

/** Trims and collapses runs of whitespace to one space; case is kept. */
export const normalizeVaultName = (name: string) => name.trim().replace(/\s+/g, ' ')

export const VAULT_NAME_RULE = 'Use 3–32 letters, numbers and spaces, with at least one letter.'

export const vaultNameSchema = z
  .string({ error: 'Enter a name' })
  .transform(normalizeVaultName)
  .pipe(
    z
      .string()
      .min(VAULT_NAME_MIN, `Use at least ${VAULT_NAME_MIN} characters`)
      .max(VAULT_NAME_MAX, `Use at most ${VAULT_NAME_MAX} characters`)
      .regex(/^[\p{L}\p{N} ]+$/u, 'Use only letters, numbers and spaces')
      .regex(/\p{L}/u, 'Include at least one letter'),
  )

/**
 * The uniqueness key for a Vault name: normalised, lowercased, spaces to
 * hyphens. "Alice Resells", "alice RESELLS" and "alice   resells" all give
 * "alice-resells". The organization table's unique slug index enforces it.
 */
export const vaultSlug = (name: string) => normalizeVaultName(name).toLowerCase().replace(/ /g, '-')

/** @deprecated Use `vaultNameSchema`; kept for callers that still say "workspace". */
export const workspaceNameSchema = vaultNameSchema

/** Deleting a workspace's data is confirmed by typing the workspace's name. */
export const deleteWorkspaceDataBody = z.object({
  confirm: z.string().trim().min(1).max(200).describe("The active workspace's name, as a confirmation"),
})

export const passwordSchema = z
  .string()
  .min(10, 'Use at least 10 characters')
  .max(128, 'Use at most 128 characters')
